import { useEffect, useRef } from "react";

// Some pages scroll an inner `overflow-y-auto` div instead of the
// window (Compare's `.vi-cmp` main column, Dynamic Graphs' `.left-
// section`) — `window.scrollY`/`window.scrollTo` are no-ops there, so
// the scroll position has to be read from and restored on whichever
// ancestor is actually the scrolling element.
const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
};

const PIN_DURATION_MS = 2000;

/**
 * Attach `containerRef` to a DOM node inside the page's real scroll
 * container, then call `pinScroll()` right before a filter/dropdown
 * change that remounts or resizes the content below it. A single
 * restore can't reliably outrun either a delayed focus-return scroll
 * (an antd Select closing its dropdown) or a loading-placeholder →
 * real-content resize once async data lands, so this re-applies the
 * captured scroll position every frame for a couple of seconds instead
 * — and backs off automatically if the user tries to scroll for real.
 */
export const useScrollPin = <T extends HTMLElement>() => {
  const containerRef = useRef<T>(null);
  const tokenRef = useRef<symbol | null>(null);

  useEffect(() => {
    return () => {
      tokenRef.current = null;
    };
  }, []);

  const pinScroll = () => {
    const scrollParent = findScrollParent(containerRef.current);
    const scrollTop = scrollParent ? scrollParent.scrollTop : window.scrollY;
    const restoreScroll = () => {
      if (scrollParent) {
        scrollParent.scrollTop = scrollTop;
      } else {
        window.scrollTo(0, scrollTop);
      }
    };

    const token = Symbol("scroll-pin");
    tokenRef.current = token;

    const cancel = () => {
      if (tokenRef.current === token) {
        tokenRef.current = null;
      }
    };
    const scrollEventTarget: EventTarget = scrollParent ?? window;
    scrollEventTarget.addEventListener("wheel", cancel, {
      passive: true,
      once: true,
    });
    scrollEventTarget.addEventListener("touchstart", cancel, {
      passive: true,
      once: true,
    });

    const startedAt = performance.now();
    const tick = () => {
      if (tokenRef.current !== token) return;
      restoreScroll();
      if (performance.now() - startedAt < PIN_DURATION_MS) {
        requestAnimationFrame(tick);
      }
    };
    requestAnimationFrame(tick);
  };

  return { containerRef, pinScroll };
};
